import { type FormEvent, type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal } from 'lucide-react'
import { LogoIcon } from '@/assets/Logo'
import { useAuth } from '@/hooks/use-auth'
import { buildCompactPageItems } from '@/features/products/search-filters'
import { AiOverview } from './ai-overview'
import { citedProducts, useAiOverview, type AiOverviewRequest } from './ai-overview-api'
import { StorefrontAutocomplete } from './storefront-autocomplete'
import { StorefrontFilters } from './storefront-filters'
import { formatCount, storefrontFilterGroups } from './storefront-format'
import { StorefrontProductCard } from './storefront-product-card'
import { SearchTips } from './search-tips'
import { StorefrontChat, type FollowUp } from './storefront-chat'
import type { SearchOverviewChat } from './storefront-chat-api'
import {
  appliedFilterNames,
  isStillApplied,
  smartSearchQueryOptions,
  stateFromDecision,
  useSmartSearchDecision,
  withoutApplied,
} from './smart-search-api'
import { SmartSearchStrip, type SmartStatus } from './smart-search-strip'
import { classifySearchQuery, isModelSearch, type SearchMode } from './search-mode'
import {
  filterOptionsQueryOptions,
  type DiscoveryRequest,
  type RangeSelection,
  type StorefrontScope,
  type StorefrontSort,
} from './storefront-api'
import {
  activeFilterCount,
  searchFromState,
  stateFromSearch,
  type CatalogSearch,
  type DiscoveryState,
} from './search-state'
import './storefront.css'

const PER_PAGE = 24
const RANK_SORT = '__rank'

// The storefront's breakpoints apply to this frame, which sits beside the dashboard sidebar.
function useElementWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(1440)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])
  return width
}
const SORT_OPTIONS: Array<{ value: StorefrontSort; label: string }> = [
  { value: 'featured', label: 'Featured' },
  { value: 'price_desc', label: 'Price high-to-low' },
  { value: 'price_asc', label: 'Price low-to-high' },
  { value: 'relevance', label: 'Relevance' },
  { value: 'newest', label: 'Newest' },
]

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <nav className="sf-pagination" aria-label="Product results pages">
      <button
        type="button"
        className="sf-pagination__item"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous product results page"
      >
        <ChevronLeft size={16} strokeWidth={1.5} />
      </button>
      {buildCompactPageItems(page, totalPages).map((item) =>
        typeof item === 'number' ? (
          <button
            type="button"
            key={item}
            className={`sf-pagination__item ${item === page ? 'is-active' : ''}`}
            aria-current={item === page ? 'page' : undefined}
            onClick={() => onPageChange(item)}
            aria-label={`Product results page ${item}`}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="sf-pagination__item" aria-hidden="true">
            …
          </span>
        ),
      )}
      <button
        type="button"
        className="sf-pagination__item"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next product results page"
      >
        <ChevronRight size={16} strokeWidth={1.5} />
      </button>
    </nav>
  )
}

function ProductGridSkeleton() {
  return (
    <div className="sf-grid" aria-label="Loading products">
      {Array.from({ length: 9 }, (_, index) => (
        <div className="sf-card sf-card--skeleton" key={index} aria-hidden="true">
          <span className="sf-skeleton sf-skeleton--image" />
          <span className="sf-skeleton" />
          <span className="sf-skeleton sf-skeleton--short" />
          <span className="sf-skeleton" />
        </div>
      ))}
    </div>
  )
}

export function CatalogSearchPage({
  search,
  onSearchChange,
}: {
  search: CatalogSearch
  onSearchChange: (search: CatalogSearch) => void
}) {
  const state = useMemo(() => stateFromSearch(search), [search])
  const { query, scope } = state
  // Smart filters can carry a search entirely in filters, with no words left.
  const canRequest = Boolean(query || scope || state.ai)
  const { getAccessToken } = useAuth()
  const decide = useSmartSearchDecision()
  // The shopper's pick on the search-bar switch, until the search runs (USA-1255).
  const [modeOverride, setModeOverride] = useState<SearchMode | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const frameWidth = useElementWidth(frameRef)
  // After an AI search the boxes keep showing what was typed.
  const shownQuery = state.ai || query
  const [headerDraft, setHeaderDraft] = useState(shownQuery)
  const [draftQuery, setDraftQuery] = useState(shownQuery)
  const [syncedQuery, setSyncedQuery] = useState(shownQuery)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  // "Ask a follow-up" (USA-1257): the store chat opened with an overview, kept until the next one.
  const [followUp, setFollowUp] = useState<FollowUp | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const askFollowUp = (chat: SearchOverviewChat, question: string | null = null) => {
    setFollowUp((current) => ({ chat, question, nonce: (current?.nonce ?? 0) + 1 }))
    setChatOpen(true)
  }

  // Back/forward navigation changes the URL query; mirror it into both search boxes.
  if (syncedQuery !== shownQuery) {
    setSyncedQuery(shownQuery)
    setHeaderDraft(shownQuery)
    setDraftQuery(shownQuery)
    setModeOverride(null)
  }
  const resultsMode: SearchMode = state.ai ? 'ai' : 'keywords'
  // The mode a search of this text would use: the switch's pick, the mode it already ran in,
  // or the rule's choice.
  const modeFor = (text: string): SearchMode => {
    const trimmed = text.trim()
    if (modeOverride) return modeOverride
    if (decide.isPending && trimmed === decide.variables) return 'ai'
    if (trimmed && trimmed === shownQuery) return resultsMode
    return classifySearchQuery(trimmed)
  }

  const request = useMemo<DiscoveryRequest>(
    () => ({
      query,
      page: state.page,
      perPage: PER_PAGE,
      sort: state.sort,
      multiSelections: state.multiSelections,
      rangeSelections: state.rangeSelections,
      ...(scope ? { scope } : {}),
      ...(state.rank ? { rank: state.rank } : {}),
    }),
    [query, scope, state.page, state.sort, state.multiSelections, state.rangeSelections, state.rank],
  )
  // Like Google's, the overview answers the words as typed on the first results page of an AI
  // search; a model-number search gets one in either mode.
  const overviewQuery = shownQuery
  const modelSearch = isModelSearch(overviewQuery)
  const showOverview = resultsMode === 'ai' || modelSearch
  const overviewRequest = useMemo<AiOverviewRequest | null>(
    () =>
      overviewQuery && showOverview && state.page === 1
        ? {
            query: overviewQuery,
            placement: 'results',
            filters: state.multiSelections,
            ranges: state.rangeSelections,
            ...(scope ? { scope } : {}),
            ...(modelSearch ? { modelSearch } : {}),
          }
        : null,
    [
      overviewQuery,
      showOverview,
      modelSearch,
      scope,
      state.page,
      state.multiSelections,
      state.rangeSelections,
    ],
  )
  const overview = useAiOverview(overviewRequest)
  const filterRequest = useMemo<DiscoveryRequest>(
    () => ({ ...request, page: 1, sort: undefined }),
    [request],
  )

  // Results come from the staff smart search: the store search plus filters it lacks and ship time.
  const searchQuery = useQuery({
    ...smartSearchQueryOptions(getAccessToken, request),
    enabled: canRequest,
  })
  const searchData = searchQuery.data ?? null
  const scopeSearchReady = !scope || searchData !== null
  const filterQuery = useQuery({
    ...filterOptionsQueryOptions(filterRequest),
    enabled: canRequest && scopeSearchReady,
  })
  const filterData = filterQuery.data ?? null
  const loading = searchQuery.isFetching
  const filtersLoading =
    filterQuery.isFetching || (canRequest && Boolean(scope) && searchQuery.isPending)

  useEffect(() => {
    if (!mobileFiltersOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [mobileFiltersOpen])

  const commit = (next: DiscoveryState) => onSearchChange(searchFromState(next))

  const toggleOption = (key: string, value: string) => {
    const current = state.multiSelections[key] ?? []
    const values = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]
    commit({
      ...state,
      page: 1,
      multiSelections: { ...state.multiSelections, [key]: values },
    })
  }

  const applyRange = (key: string, selection: RangeSelection) => {
    const rangeSelections = { ...state.rangeSelections }
    if (selection.min || selection.max) rangeSelections[key] = selection
    else delete rangeSelections[key]
    commit({ ...state, page: 1, rangeSelections })
  }

  const clearAll = () => commit({ ...state, page: 1, multiSelections: {}, rangeSelections: {} })

  // The header behaves like the storefront header: a fresh search or collection page.
  const freshState = (next: Partial<DiscoveryState>): DiscoveryState => ({
    query: '',
    page: 1,
    sort: 'featured',
    multiSelections: {},
    rangeSelections: {},
    ...next,
  })
  // An AI search goes through smart filters first; a failure falls back to the words alone.
  const runSearch = (nextQuery: string, mode = modeFor(nextQuery)) => {
    setModeOverride(null)
    if (mode === 'keywords' || !nextQuery.trim()) {
      decide.reset()
      commit(freshState({ query: nextQuery }))
      return
    }
    decide.mutate(nextQuery.trim(), {
      onSuccess: (decision) => commit(stateFromDecision(decision)),
      onError: () => commit(freshState({ query: nextQuery })),
    })
  }
  const headerSearch = (nextQuery: string, mode?: SearchMode) => runSearch(nextQuery, mode)
  const changeHeaderDraft = (value: string) => {
    setHeaderDraft(value)
    if (!value.trim()) setModeOverride(null)
  }
  // Switching on the search that is showing runs it again in that mode.
  const changeMode = (mode: SearchMode) => {
    const text = headerDraft.trim()
    if (!scope && text && text === shownQuery && mode !== modeFor(text)) {
      setSuggestOpen(false)
      runSearch(text, mode)
    } else setModeOverride(mode)
  }
  const openScope = (nextScope: StorefrontScope) => {
    setHeaderDraft('')
    commit(freshState({ scope: nextScope }))
  }

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // A keyword search refines the current filters, as on the store.
    if (scope || modeFor(draftQuery) === 'keywords') {
      setModeOverride(null)
      commit({ ...state, ai: undefined, query: draftQuery.trim(), page: 1 })
    } else runSearch(draftQuery.trim())
  }

  const smartStatus: SmartStatus = decide.isPending
    ? 'pending'
    : decide.isError
      ? 'error'
      : state.ai
        ? 'ready'
        : 'idle'
  const smartDecision = decide.data && decide.data.query === state.ai ? decide.data : null
  const smartApplied = smartDecision
    ? smartDecision.applied.filter((item) => isStillApplied(item, state))
    : []
  const smartRelaxed = smartDecision ? smartDecision.relaxed : []
  const smartNames = useMemo(
    () => (smartDecision ? appliedFilterNames(smartDecision) : {}),
    [smartDecision],
  )

  const changePage = (page: number) => {
    if (page < 1 || page === state.page) return
    commit({ ...state, page })
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const filterGroups = useMemo(
    () => storefrontFilterGroups(filterData, scope?.key),
    [filterData, scope?.key],
  )
  const total = searchData?.total ?? filterData?.total ?? 0
  const products = searchData?.products ?? []
  const selectedCount = activeFilterCount(state)
  const emptyUnfilteredScope =
    Boolean(scope) && searchData !== null && total === 0 && selectedCount === 0 && !query
  // When the words match nothing, the products the overview names become the answer.
  const noMatches = !loading && !searchQuery.isError && canRequest && products.length === 0
  const answerProducts = noMatches ? citedProducts(overview) : []
  const overviewWorking = overview?.phase === 'loading' || overview?.phase === 'streaming'
  // Nothing matched and nothing is selected, so there is nothing to filter.
  const withoutFacets = emptyUnfilteredScope || (noMatches && selectedCount === 0)
  const scopeLabel = scope?.label || 'this collection'
  const searchLabel = scope ? `Search within ${scopeLabel}` : 'Update your product search'

  return (
    <div
      ref={frameRef}
      className="storefront sf-frame"
      data-compact={frameWidth <= 1024 || undefined}
      data-phone={frameWidth <= 749 || undefined}
      data-narrow={frameWidth <= 480 || undefined}
      data-e2e="catalog-search"
    >
      <header className="sf-header">
        <div className="sf-width sf-header__inner">
          <span className="sf-header__logo" aria-label="USA Restaurant Suppliers">
            <LogoIcon />
          </span>
          <StorefrontAutocomplete
            value={headerDraft}
            onValueChange={changeHeaderDraft}
            open={suggestOpen}
            onOpenChange={setSuggestOpen}
            onSearch={headerSearch}
            onScope={openScope}
            largeScreen={frameWidth > 1024}
            narrow={frameWidth <= 480}
            mode={modeFor(headerDraft)}
            onModeChange={changeMode}
            onFollowUp={askFollowUp}
          />
        </div>
      </header>

      <div className="sf-main">
        {suggestOpen && headerDraft.trim() ? (
          <div className="sf-overlay" aria-hidden="true" />
        ) : null}
        <div ref={rootRef} className="sf-width sf-discovery">
          <div className="sf-heading">
            <h1>{scope ? scope.label : 'Search products'}</h1>
            <span className="sf-heading__rule" aria-hidden="true" />
          </div>

          <AiOverview
            request={overviewRequest}
            state={overview}
            answer={answerProducts.length > 0}
            onFollowUp={askFollowUp}
          />

          <div className={`sf-disco ${withoutFacets ? 'sf-disco--without-facets' : ''}`}>
            {!withoutFacets ? (
              <StorefrontFilters
                groups={filterGroups}
                state={state}
                smartNames={smartNames}
                total={total}
                loading={filtersLoading}
                updating={
                  (loading && searchData !== null) || (filtersLoading && filterData !== null)
                }
                error={filterQuery.isError}
                mobileOpen={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                onToggle={toggleOption}
                onRange={applyRange}
                onClearAll={clearAll}
              />
            ) : null}

            <div
              className={`sf-disco__main ${loading && searchData ? 'is-loading' : ''}`}
              aria-busy={loading && searchData ? 'true' : 'false'}
            >
              <form
                className="sf-search"
                role="search"
                aria-label={searchLabel}
                onSubmit={submitSearch}
              >
                <label className="sf-search__label" htmlFor="catalog-search-query">
                  {searchLabel}
                </label>
                <div className="sf-search__controls">
                  <div className="sf-search__field">
                    <input
                      id="catalog-search-query"
                      className="sf-input sf-search__input"
                      type="search"
                      value={draftQuery}
                      maxLength={200}
                      placeholder={scope ? `Search ${scopeLabel}` : 'Search products'}
                      onChange={(event) => setDraftQuery(event.target.value)}
                      autoComplete="off"
                    />
                    {!scope ? (
                      <SearchTips
                        className="sf-search__tips"
                        onPick={(nextQuery) => {
                          setDraftQuery(nextQuery)
                          runSearch(nextQuery)
                        }}
                      />
                    ) : null}
                  </div>
                  <button className="sf-button sf-button--primary sf-search__button" type="submit">
                    <Search size={18} strokeWidth={1.5} aria-hidden="true" />
                    <span>Search</span>
                  </button>
                </div>
              </form>

              {!scope &&
              (smartStatus === 'pending' ||
                smartStatus === 'error' ||
                smartApplied.length ||
                smartRelaxed?.length) ? (
                <SmartSearchStrip
                  status={smartStatus}
                  pendingQuery={decide.variables}
                  applied={smartApplied}
                  relaxed={smartRelaxed}
                  onRemove={(item) => commit(withoutApplied(item, state))}
                />
              ) : null}

              <div className={`sf-toolbar ${answerProducts.length ? 'sf-toolbar--answer' : ''}`}>
                {!withoutFacets ? (
                  <button
                    className="sf-toolbar__filters"
                    type="button"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <span>Filters</span>
                    <SlidersHorizontal size={18} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                ) : null}
                {answerProducts.length ? null : (
                  <>
                    <div className="sf-toolbar__count" aria-live="polite">
                      {(loading && !searchData) || (!canRequest && decide.isPending) ? (
                        'Loading products…'
                      ) : (
                        <>
                          Showing <b>{formatCount(total)}</b> products
                        </>
                      )}
                    </div>
                    <div className="sf-toolbar__sort">
                      <label htmlFor="catalog-search-sort">Sort by</label>
                      <select
                        id="catalog-search-sort"
                        className={state.rank ? 'sf-toolbar__sort--rank' : undefined}
                        value={state.rank ? RANK_SORT : state.sort}
                        disabled={!canRequest || loading}
                        onChange={(event) => {
                          if (event.target.value === RANK_SORT) return
                          commit({
                            ...state,
                            rank: undefined,
                            page: 1,
                            sort: event.target.value as StorefrontSort,
                          })
                        }}
                      >
                        {state.rank ? (
                          <option value={RANK_SORT}>
                            {state.rank.label}{' '}
                            {state.rank.direction === 'desc' ? 'high-to-low' : 'low-to-high'}
                          </option>
                        ) : null}
                        {SORT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {!canRequest && !decide.isPending ? (
                <p className="sf-notice">Enter a search term to find products.</p>
              ) : null}
              {!canRequest && decide.isPending ? <ProductGridSkeleton /> : null}
              {searchQuery.isError ? (
                <div className="sf-notice sf-notice--error" role="alert">
                  <p>Product results could not be loaded. Please try again.</p>
                  <button
                    type="button"
                    className="sf-button sf-button--primary sf-button--small"
                    onClick={() => {
                      void searchQuery.refetch()
                      void filterQuery.refetch()
                    }}
                  >
                    Try again
                  </button>
                </div>
              ) : null}
              {loading && !searchData && !searchQuery.isError ? <ProductGridSkeleton /> : null}
              {noMatches && !answerProducts.length && !overviewWorking ? (
                <div className="sf-notice">
                  <p>No products match this search and filter combination.</p>
                  {!scope ? (
                    <p className="sf-muted">
                      Try a broader search or remove one of the active filters.
                    </p>
                  ) : null}
                  {!scope && selectedCount > 0 ? (
                    <button
                      type="button"
                      className="sf-button sf-button--secondary sf-button--small"
                      onClick={clearAll}
                    >
                      Clear all filters
                    </button>
                  ) : null}
                </div>
              ) : null}
              {answerProducts.length ? (
                <div className="sf-grid" data-e2e="ai-answer-results">
                  {answerProducts.map((product) => (
                    <StorefrontProductCard key={product.uuid} product={product} onScope={openScope} />
                  ))}
                </div>
              ) : null}
              {products.length > 0 ? (
                <div className="sf-grid" data-e2e="search-results">
                  {products.map((product) => (
                    <StorefrontProductCard
                      key={product.shopify_id || product.uuid}
                      product={product}
                      onScope={openScope}
                    />
                  ))}
                </div>
              ) : null}
              {searchData ? (
                <Pagination
                  page={searchData.page}
                  totalPages={searchData.total_pages}
                  onPageChange={changePage}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {followUp ? (
        <StorefrontChat
          key={followUp.chat.signature}
          followUp={followUp}
          open={chatOpen}
          onOpen={() => setChatOpen(true)}
          onClose={() => setChatOpen(false)}
        />
      ) : null}
    </div>
  )
}
